import { useState } from 'react';
import { ChevronDown, ChevronRight, Pause, XCircle } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { SlidePanel } from '../../components/common/SlidePanel';
import { PROJECT_STATUSES, STATUS_FLOW } from './config';
import { usePhases, useTasks, useGates, useQuotes, usePhotos, useExpenses, usePayments, useAuditTrail } from './detailHooks';
import { PhaseSection } from './PhaseSection';
import { QuoteSection } from './QuoteSection';
import { PhotosSection, ExpensesSection, PaymentsSection, TrustBadgeSection, AuditTrailSection } from './DetailSections';
import type { ProjectRow, TeamUser } from './hooks';

interface Props {
  project: ProjectRow | null;
  businessId: string;
  userId: string;
  users: TeamUser[];
  onClose: () => void;
  onAdvance: (id: string, status: string) => void;
  onHold: (id: string, reason: string) => void;
  onCancel: (id: string, reason: string) => void;
}

function fmt$(n: number): string { return `$${n.toLocaleString('en-CA', { minimumFractionDigits: 0 })}`; }

function Section({ title, defaultOpen, children }: { title: string; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  return (
    <div style={{ borderTop: `1px solid ${palette.g150}`, paddingTop: 12, marginBottom: 12 }}>
      <button onClick={() => setOpen(!open)} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: open ? 10 : 0, fontFamily: 'inherit' }}>
        {open ? <ChevronDown size={14} color={palette.g500} /> : <ChevronRight size={14} color={palette.g500} />}
        <span style={{ fontSize: 13, fontWeight: 600, color: palette.g700 }}>{title}</span>
      </button>
      {open && children}
    </div>
  );
}

export function ProjectDetailPanel({ project, businessId, userId, users, onClose, onAdvance, onHold, onCancel }: Props) {
  const pid = project?.id ?? null;
  const { phases, createPhase } = usePhases(pid, businessId);
  const { tasks, createTask, toggleTask, assignTask } = useTasks(pid, businessId);
  const { gates, createGate, resolveGate } = useGates(pid, businessId);
  const { quotes, lineItems, fetchLineItems, createQuote, addLineItem, removeLineItem, updateLineItem, sendQuote, updateQuoteStatus, updatePaymentSchedule } = useQuotes(pid, businessId);
  const { grouped: photoGroups, upload: uploadPhoto } = usePhotos(pid);
  const { expenses, total: expenseTotal, logExpense } = useExpenses(pid, businessId);
  const { payments, totalPaid, recordPayment } = usePayments(pid, businessId);
  const { entries: auditEntries } = useAuditTrail(pid);

  const [holdReason, setHoldReason] = useState('');
  const [showHold, setShowHold] = useState(false);
  const [showCancel, setShowCancel] = useState(false);

  if (!project) return null;

  const st = PROJECT_STATUSES[project.status] ?? PROJECT_STATUSES.lead;
  const nextIdx = STATUS_FLOW.indexOf(project.status) + 1;
  const nextStatus = nextIdx > 0 && nextIdx < STATUS_FLOW.length ? STATUS_FLOW[nextIdx] : null;
  const isActive = STATUS_FLOW.includes(project.status) && project.status !== 'closed';
  const spent = expenseTotal;
  const budgetPct = project.budget ? Math.min(100, Math.round(spent / project.budget * 100)) : 0;

  return (
    <SlidePanel isOpen={!!project} onClose={onClose} title={project.name} subtitle={`#${project.id.slice(0, 8)}`} width={620}>
      {/* Header */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ padding: '3px 10px', borderRadius: 4, fontSize: 12, fontWeight: 600, background: st.bg, color: st.fg }}>{st.label}</span>
        {isActive && nextStatus && (
          <button onClick={() => onAdvance(project.id, nextStatus)} style={{ marginLeft: 'auto', padding: '5px 14px', borderRadius: 6, border: 'none', background: palette.accent, color: palette.white, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            Advance to {PROJECT_STATUSES[nextStatus]?.label ?? nextStatus}
          </button>
        )}
      </div>

      {/* Status pipeline */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
        {STATUS_FLOW.map(s => {
          const active = STATUS_FLOW.indexOf(project.status) >= STATUS_FLOW.indexOf(s);
          return <div key={s} style={{ flex: 1, height: 4, borderRadius: 2, background: active ? palette.accent : palette.g200 }} />;
        })}
      </div>

      {/* Details grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16, fontSize: 13 }}>
        <div><span style={{ color: palette.g400 }}>Client</span><div style={{ fontWeight: 500, color: palette.g800, marginTop: 2 }}>{project.client_display ?? '—'}</div></div>
        <div><span style={{ color: palette.g400 }}>Manager</span><div style={{ fontWeight: 500, color: palette.g800, marginTop: 2 }}>{project.manager_name ?? '—'}</div></div>
        <div><span style={{ color: palette.g400 }}>Dates</span><div style={{ fontWeight: 500, color: palette.g800, marginTop: 2 }}>{project.start_date ?? project.proposed_start ?? '—'} &rarr; {project.target_end_date ?? '—'}</div></div>
        <div><span style={{ color: palette.g400 }}>Location</span><div style={{ fontWeight: 500, color: palette.g800, marginTop: 2 }}>{project.location ?? project.address ?? '—'}</div></div>
      </div>

      {/* Budget bar */}
      {project.budget && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: palette.g500, marginBottom: 4 }}>
            <span>{fmt$(spent)} spent</span><span>{fmt$(project.budget)} budget</span>
          </div>
          <div style={{ height: 6, borderRadius: 3, background: palette.g200 }}>
            <div style={{ height: 6, borderRadius: 3, background: budgetPct > 90 ? palette.red : palette.accent, width: `${budgetPct}%`, transition: 'width 0.3s' }} />
          </div>
        </div>
      )}

      {/* Status actions */}
      {isActive && project.status !== 'on_hold' && project.status !== 'cancelled' && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {!showHold && !showCancel && (
            <>
              <button onClick={() => setShowHold(true)} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 12px', borderRadius: 6, border: `1px solid ${palette.amber}`, background: palette.amberWash, fontSize: 11, fontWeight: 600, color: palette.amber, cursor: 'pointer', fontFamily: 'inherit' }}><Pause size={12} /> Hold</button>
              <button onClick={() => setShowCancel(true)} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 12px', borderRadius: 6, border: `1px solid ${palette.red}`, background: palette.redWash, fontSize: 11, fontWeight: 600, color: palette.red, cursor: 'pointer', fontFamily: 'inherit' }}><XCircle size={12} /> Cancel</button>
            </>
          )}
          {showHold && (
            <div style={{ display: 'flex', gap: 6, flex: 1, alignItems: 'center' }}>
              <input style={{ flex: 1, padding: '5px 8px', borderRadius: 4, border: `1px solid ${palette.g200}`, fontSize: 12, fontFamily: 'inherit' }} value={holdReason} onChange={e => setHoldReason(e.target.value)} placeholder="Reason for hold..." />
              <button onClick={() => { onHold(project.id, holdReason); setShowHold(false); setHoldReason(''); }} style={{ padding: '5px 10px', borderRadius: 4, border: 'none', background: palette.amber, color: palette.white, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Confirm Hold</button>
              <button onClick={() => setShowHold(false)} style={{ padding: '5px 8px', borderRadius: 4, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 11, color: palette.g500, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
            </div>
          )}
          {showCancel && (
            <div style={{ display: 'flex', gap: 6, flex: 1, alignItems: 'center' }}>
              <input style={{ flex: 1, padding: '5px 8px', borderRadius: 4, border: `1px solid ${palette.g200}`, fontSize: 12, fontFamily: 'inherit' }} value={holdReason} onChange={e => setHoldReason(e.target.value)} placeholder="Reason for cancellation..." />
              <button onClick={() => { onCancel(project.id, holdReason); setShowCancel(false); setHoldReason(''); }} style={{ padding: '5px 10px', borderRadius: 4, border: 'none', background: palette.red, color: palette.white, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Confirm Cancel</button>
              <button onClick={() => setShowCancel(false)} style={{ padding: '5px 8px', borderRadius: 4, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 11, color: palette.g500, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
            </div>
          )}
        </div>
      )}

      {project.description && <div style={{ fontSize: 13, color: palette.g600, marginBottom: 12, padding: '10px 12px', background: palette.g50, borderRadius: 6 }}>{project.description}</div>}

      {/* 7 collapsible sections */}
      <Section title="Phases" defaultOpen>
        <PhaseSection phases={phases} tasks={tasks} gates={gates} users={users} userId={userId}
          onToggleTask={(id, s) => toggleTask(id, s, userId)} onAssignTask={(id, a) => assignTask(id, a, userId)}
          onCreateTask={(phId, n, o) => createTask(phId, n, o, userId)} onCreatePhase={(n, o) => createPhase(n, o, userId)}
          onResolveGate={(gId, act, note) => resolveGate(gId, act, note, userId)} onCreateGate={(n, gt, aph, o) => createGate(n, gt, aph, o, userId)} />
      </Section>

      <Section title="Quote" defaultOpen>
        <QuoteSection quotes={quotes} lineItems={lineItems} userId={userId}
          onCreateQuote={() => createQuote(userId)} onAddLineItem={(qId, item) => addLineItem(qId, item, userId)}
          onRemoveLineItem={(iId, qId) => removeLineItem(iId, qId, userId)} onUpdateLineItem={(iId, data, qId) => updateLineItem(iId, data, qId)}
          onSendQuote={(qId) => sendQuote(qId, userId)} onUpdateQuoteStatus={(qId, s) => updateQuoteStatus(qId, s, userId)}
          onUpdatePaymentSchedule={updatePaymentSchedule} onFetchLineItems={fetchLineItems} />
      </Section>

      <Section title="Photos">
        <PhotosSection grouped={photoGroups} onUpload={(file, tag) => uploadPhoto(file, tag, userId)} />
      </Section>

      <Section title="Expenses">
        <ExpensesSection expenses={expenses} total={expenseTotal} budget={project.budget} onLog={(data) => logExpense(data, userId)} />
      </Section>

      <Section title="Payments">
        <PaymentsSection payments={payments} totalPaid={totalPaid} onRecord={(t, a, m) => recordPayment(t, a, m, userId)} />
      </Section>

      <Section title="Trust Badge">
        <TrustBadgeSection gpsVerified={project.gps_verified} hasBefore={project.has_before_photos}
          hasAfter={project.has_after_photos} clientConfirmed={project.client_confirmed}
          isPaid={project.status === 'paid' || project.status === 'closed'} />
      </Section>

      <Section title="Activity">
        <AuditTrailSection entries={auditEntries} />
      </Section>

      <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 16, borderTop: `1px solid ${palette.g100}` }}>
        <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 13.5, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit' }}>Close</button>
      </div>
    </SlidePanel>
  );
}
